import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Flex } from '@rebass/grid'
import styled from 'styled-components'
import { palette, size } from 'styled-theme'
import moment from 'moment'
import { useTimer } from 'react-timer-hook'
import Text from '../../atoms/Text'
import Image from '../../atoms/Image'
import Button from '../../atoms/Button'
import AdSense from '../../atoms/AdSense'
import Viewport from '../../atoms/Viewport'
import ContentBox from '../../atoms/ContentBox'
import Modal from '../../molecules/Modal'
import AdSenseGroupView from '../../molecules/AdSenseGroupView'
import TopUpPayment from '../../organisms/TopUpPayment'
import { handleErrorResponse } from '../../../utlils/general'
import { datetimeFormatter } from '../../../utlils/datetime'
import { useAuth } from '../../../contexts/auth'
import { useLoading } from '../../../contexts/loading'
import { getHotPicks, sendHotPickLike } from '../../../api/hot-picks'
import imgFemale from '../../../assets/images/female.png'
import imgMale from '../../../assets/images/male.png'
import ActionButton from './ActionButton'

const HOT_PICK_LIKE_COST = 12

const StyledViewport = styled(Viewport)`
  padding: 4.5rem 3rem 3rem;
  @media only screen and (max-width: ${size('mobile')}) {
    padding: 4rem 0 0;
  }
`

const StyledContentBox = styled(ContentBox)`
  padding: 0.5rem;
  padding-bottom: 2rem;
`

const HotPickItemContainer = styled(Flex)`
  align-items: center;
  justify-content: center;
  flex-direction: column;
  position: relative;
  border: 2px solid ${palette('primary', 0)};
  background-color: ${palette('primary', 5)};
  border-radius: 0.5rem;
  overflow: hidden;
  aspect-ratio: 1;
  flex-shrink: 0;
`

const ExplorePage = () => {
  const [hotPicks, setHotPicks] = useState([])
  const [actionModalOpen, setActionModalOpen] = useState(false)
  const [actionHotPickId, setActionHotPickId] = useState(null)
  const [topUpModalOpen, setTopUpModalOpen] = useState(false)
  const { setLoading } = useLoading()
  const { auth, setAuth } = useAuth()

  const emailVerified = useMemo(() => {
    return auth.user?.email_verified || false
  }, [auth.user])

  const refreshDate = useMemo(() => {
    const currentDate = moment(datetimeFormatter(new Date(), 'YYYY-MM-DDT00:00:00+08:00'))
    const nextDate = currentDate.clone().add(1, 'day')
    return nextDate.toDate()
  }, [])

  const fetchHotPicksFromApi = useCallback(() => {
    return getHotPicks()
      .then(({ data }) => {
        setHotPicks(data)
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth)
      })
  }, [setAuth])

  const onClickConfirmLike = useCallback(() => {
    const messageRemaining = auth.user?.message_remaining || 0
    if (messageRemaining < HOT_PICK_LIKE_COST) {
      setTopUpModalOpen(true)
      return
    }
    setLoading(true)
    return sendHotPickLike(actionHotPickId, {})
      .then(({ data }) => {
        setActionHotPickId(null)
        setActionModalOpen(false)
        if (data.status === 'pending') {
          return setHotPicks((picks) => picks.map((pick) => {
            if (pick.hot_pick_id !== actionHotPickId) {
              return pick
            }
            return { ...pick, user_match: data }
          }))
        }
        return fetchHotPicksFromApi()
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth, '有啲野出錯左！\n請等陣再試>.<')
      })
      .finally(() => {
        setLoading(false)
      })
  }, [actionHotPickId, auth.user, fetchHotPicksFromApi, setLoading, setAuth])

  const onClickLike = useCallback((hotPickId) => {
    setActionHotPickId(hotPickId)
    setActionModalOpen(true)
  }, [])

  const onRefreshTimesUp = useCallback(() => {
    setLoading(true)
    fetchHotPicksFromApi()
      .finally(() => {
        setLoading(false)
      })
  }, [fetchHotPicksFromApi, setLoading])

  const { seconds, minutes, hours } = useTimer({
    expiryTimestamp: refreshDate,
    onExpire: onRefreshTimesUp,
  })

  useEffect(() => {
    setLoading(true)
    fetchHotPicksFromApi()
      .finally(() => {
        setLoading(false)
      })
  }, [fetchHotPicksFromApi, setLoading])

  return (
    <StyledViewport>
      <StyledContentBox>
        <Flex justifyContent="flex-end" alignItems="center" mb="0.5rem">
          <Text variant="body2">下次更新: &nbsp;</Text>
          <Text>
            {hours}:{`${minutes}`.padStart(2, '0')}:{`${seconds}`.padStart(2, '0')}
          </Text>
          <Box className="self_rotate" ml="0.5rem">
            <Text>⏳</Text>
          </Box>
        </Flex>
        <Flex flexDirection="column" width={1}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              columnGap: '0.5rem',
              rowGap: '0.5rem',
            }}
          >
            {(hotPicks || []).map((hotPick, hIdx) => (
              <HotPickItemContainer key={hIdx}>
                {hotPick.avatar_url ? (
                  <>
                    <Flex width={1} flex={1} flexShrink={0} style={{ position: 'relative', overflow: 'hidden' }}>
                      <Image
                        width="100%"
                        height="100%"
                        src={hotPick.avatar_url}
                        style={{
                          objectFit: 'cover',
                          objectPosition: 'top',
                        }}
                      />
                      {!hotPick.is_self && (
                        <ActionButton hotPick={hotPick} onSendLike={onClickLike} />
                      )}
                    </Flex>
                    {!!hotPick.description && (
                      <Flex
                        width={1}
                        p="0.25rem"
                        justifyContent="flex-start"
                        style={{ flexShrink: 0, maxHeight: '30%', overflow: 'auto' }}
                      >
                        <Text variant="body2">{hotPick.description}</Text>
                      </Flex>
                    )}
                  </>
                ) : (
                  <>
                    <Flex padding={16} style={{ overflow: 'auto' }}>
                      <Text variant="body1">{hotPick.description}</Text>
                    </Flex>
                    <Image
                      width="10%"
                      height="10%"
                      src={hotPick.sex === 'M' ? imgMale : imgFemale}
                      style={{ position: 'absolute', top: '0.4rem', right: '0.4rem' }}
                    />
                    <ActionButton hotPick={hotPick} onSendLike={onClickLike} />
                  </>
                )}
              </HotPickItemContainer>
            ))}
          </div>
        </Flex>
        <AdSense
          adSlotId="9477289676"
          data-full-width-responsive="false"
          style={{ height: 120, width: '100%' }}
          containerProps={{ width: 1, height: 120, mt: '2rem' }}
        />
      </StyledContentBox>
      <Modal
        isOpen={actionModalOpen}
        onClose={() => setActionModalOpen(false)}
        showCloseBtn
      >
        <Flex
          width={1}
          alignItems="center"
          flexDirection="column"
          minWidth={240}
          maxWidth={360}
          p="1rem"
        >
          {emailVerified ? (
            <Text variant="h3" align="center">
              即將發送配對邀請比對方，需要12個用量
            </Text>
          ) : (
            <>
              <Text variant="h2" align="center" palette="error">⚠️ 注意 ⚠️</Text>
              <Flex flexDirection="column" alignItems="center" marginTop="1rem">
                <Text variant="h3" palette="primary" align="center">請先驗證<b>E-mail</b> ☝️</Text>
                <Text variant="h3" palette="primary" align="center">以使用配對功能</Text>
              </Flex>
            </>
          )}
          <Flex mt="2rem" width={1} maxWidth="14.5rem">
            <Box flex={1}>
              <Button
                palette="error"
                variant="outline"
                onClick={() => setActionModalOpen(false)}
                fullWidth
              >
                返回
              </Button>
            </Box>
            <Box flex={1} ml="1rem">
              <Button
                onClick={onClickConfirmLike}
                disabled={!emailVerified}
                fullWidth
              >
                去馬！
              </Button>
            </Box>
          </Flex>
        </Flex>
      </Modal>
      <Modal
        showCloseBtn
        isOpen={topUpModalOpen}
        onClose={() => setTopUpModalOpen(false)}
        style={{ width: '100%', maxWidth: 360 }}
      >
        <Flex mb="1rem" width={1}>
          <TopUpPayment
            message={'你嘅用量不足！\n請先增值所需要嘅用量！'}
            onComplete={() => setTopUpModalOpen(false)}
          />
        </Flex>
      </Modal>
      <AdSenseGroupView />
    </StyledViewport>
  )
}

export default ExplorePage
